import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { BodyLarge400, BodyMedium400, TitleSmall500, TitleXLarge500, TitleLarge500 } from '@nebula/global-styles';
import { Button } from '@nebula/button';
import { commonUi } from '../../../constants';

export const PageContainer = styled.div<{ $isWebview?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${({ $isWebview }) => ($isWebview ? '0' : '1.5rem 0')};
  min-height: ${({ $isWebview }) =>
    $isWebview ? '100dvh' : `calc(100dvh - 64px)`};
  /* a definite height is what lets the card scroll internally and keep its buttons in view */
  height: ${({ $isWebview }) => ($isWebview ? '100dvh' : 'auto')};
  align-items: center;

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    height: auto;
    min-height: ${({ $isWebview }) => ($isWebview ? '100dvh' : 'auto')};
    justify-content: ${({ $isWebview }) => ($isWebview ? 'center' : 'initial')};
    background-color: ${({ theme }) => theme.color.ui.grey[10]};
    padding: 2rem 1.5rem 2.5rem 1.5rem;
  }
`;

export const ThankYouPageContainer = styled(PageContainer)`
  justify-content: center;
  min-height: ${({ $isWebview }) =>
    $isWebview ? '100dvh' : `calc(100dvh - ${commonUi.layout.mobileHeaderHeightPx}px)`};

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    min-height: ${({ $isWebview }) =>
      $isWebview ? '100dvh' : `calc(100dvh - ${commonUi.layout.tabletPortraitHeaderHeightPx}px)`};
  }

  @media ${({ theme }) => theme.media.smallDesktopMin} {
    min-height: ${({ $isWebview }) =>
      $isWebview ? '100dvh' : `calc(100dvh - ${commonUi.layout.smallDesktopHeaderHeightPx}px)`};
  }
`;

export const CardContainer = styled.div<{ $isWebview?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  min-height: 0;
  background: ${({ theme }) => theme.color.ui.white};
  border-radius: 1rem;
  max-width: 50rem;
  width: 100%;
  padding: 1.5rem 1rem;

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    flex: none;
    justify-content: initial;
    padding: 2rem 1.5rem;
    margin: ${({ $isWebview }) => ($isWebview ? '0 auto' : '1rem auto 0')};
  }

  @media ${({ theme }) => theme.media.tabletLandscapeMin} {
    padding: 2rem 5rem;
  }
`;

export const DetailsContainer = styled.div<{ $isWebview?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;

  /*
    In the app on a phone the card is the full screen, so the details scroll on their own and the
    buttons below them stay put. Every other case lets the page scroll as a whole.
  */
  ${({ $isWebview }) =>
    $isWebview &&
    css`
      flex: 1;
      min-height: 0;
      overflow-y: auto;

      @media ${({ theme }) => theme.media.tabletPortraitMin} {
        flex: none;
        min-height: auto;
        overflow-y: visible;
      }
    `}
`;

export const BackLinkContainer = styled.div`
  max-width: 50rem;
  width: 100%;
  margin: 0 auto;
  font-size: 1rem;
  padding-left: 1rem;
  flex-shrink: 0;

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    padding-left: 0;
  }
`;

export const Description = styled(BodyLarge400)`
  color: ${({ theme }) => theme.color.ui.grey[90]};
  margin: 0.5rem 0 0;
  line-height: 1.5rem;
  font-size: 0.875rem;
  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    font-size: 1rem;
  }
`;

export const BenefitsSection = styled.div`
  margin-top: 1.5rem;
`;

export const BenefitsBox = styled.div`
  background: ${({ theme }) => theme.color.decorative.sapphire[10]};
  border-radius: 0.75rem;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    padding: 1.5rem;
  }
`;

export const BenefitsTitle = styled(TitleSmall500)`
  color: ${({ theme }) => theme.color.ui.grey[100]};
  margin: 0;
  font-size: 1rem;
  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    font-size: 1.125rem;
  }
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.color.ui.grey[20]};
  margin: 0;
  width: 100%;
`;

export const BenefitsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    flex-direction: row;
    gap: 1rem;
  }
`;

export const BenefitsColumn = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
`;

export const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.color.ui.blue[70]};
    outline-offset: 0.125rem;
    border-radius: 0.25rem;
  }
`;

export const BenefitLabel = styled(BodyMedium400)`
  color: ${({ theme }) => theme.color.ui.grey[100]};
  line-height: 1.25rem;
  font-size: 14px;
  margin: 0;
`;

export const ButtonContainer = styled.div<{ $isWebview?: boolean }>`
  margin-top: 1.5rem;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: ${({ $isWebview }) => ($isWebview ? '0' : '1rem')};

  button {
    width: 100%;
    font-family: 'Roboto', Arial, sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.375rem;
  }

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    margin-top: 1.5rem;
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 0;

    button {
      width: auto;
    }
  }
`;

export const ConfirmButton = styled(Button)`
  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    order: 2;
  }
`;

export const BackToSubscriptionButton = styled(Button)`
  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    order: 1;
  }
`;

export const StyledTitleXLarge500 = styled(TitleXLarge500)`
  margin-top: 0;
  margin-bottom: 0.25rem;
  font-size: 1.5rem;
  line-height: 2.25rem;
  color: ${({ theme }) => theme.color.ui.grey[100]};

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    font-size: 1.75rem;
    line-height: 2.5rem;
  }
`;

export const BenefitsLink = styled.a`
  color: ${({ theme }) => theme.color.ui.blue[70]};
  text-decoration: none;
`;

export const LinkContainer = styled.div`
  display: flex;
  gap: 0.25rem;
`;

export const ViewLabel = styled(BodyMedium400)`
  margin: 0;
`;

export const ThankYouContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
  padding: ${rem(8)} ${rem(16)} ${rem(24)};
  font-family: 'Roboto', sans-serif;
  background-color: white;

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    min-height: auto;
    gap: ${rem(24)};
    padding: ${rem(32)} ${rem(80)};
    max-width: ${rem(596)};
    border-radius: ${rem(16)};
  }

  @media ${({ theme }) => theme.media.smallDesktopMin} {
    max-width: ${rem(800)};
  }
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 640px;
`;

export const ThankYouTitle = styled(TitleLarge500)`
  margin: 0;

  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    ${TitleXLarge500}
    margin-bottom: 0.5rem;
  }
`;

export const ThankYouSubtitle = styled(BodyLarge400)`
  margin: 0;
`;

export const ThankYouButton = styled(Button)`
  margin-top: 0.5rem;
  width: 100%;
  @media ${({ theme }) => theme.media.tabletPortraitMin} {
    width: fit-content;
  }
`;

export const ThankYouLinkTile = styled(BodyLarge400)`
  margin-top: 1.5rem;
`;
